import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { api, type User } from './api';

// Firebase configuration - will be injected at build time
const firebaseConfig = {
  apiKey: (window as any).__FIREBASE_API_KEY__ || '',
  authDomain: (window as any).__FIREBASE_AUTH_DOMAIN__ || '',
  projectId: (window as any).__FIREBASE_PROJECT_ID__ || '',
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

export type AuthState = {
  loading: boolean;
  user: User | null;
  firebaseUser: FirebaseUser | null;
};

type AuthListener = (state: AuthState) => void;

class AuthService {
  private state: AuthState = {
    loading: true,
    user: null,
    firebaseUser: null,
  };
  private listeners: Set<AuthListener> = new Set();

  constructor() {
    // Check for E2E test mode override
    const testUser = this.getTestModeUser();
    if (testUser) {
      this.setState({
        loading: false,
        user: testUser,
        firebaseUser: null,
      });
      return;
    }

    onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          const user = await api.createSession(idToken);
          this.setState({
            loading: false,
            user,
            firebaseUser,
          });
        } catch (error) {
          console.error('Failed to create session:', error);
          this.setState({
            loading: false,
            user: null,
            firebaseUser: null,
          });
        }
      } else {
        this.setState({
          loading: false,
          user: null,
          firebaseUser: null,
        });
      }
    });
  }

  private getTestModeUser(): User | null {
    try {
      const testUserJson = localStorage.getItem('__E2E_TEST_USER__');
      if (testUserJson) {
        return JSON.parse(testUserJson) as User;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  }

  private setState(newState: AuthState) {
    this.state = newState;
    this.listeners.forEach((listener) => listener(this.state));
  }

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async signInWithGoogle(): Promise<User | null> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const user = await api.createSession(idToken);
      return user;
    } catch (error) {
      console.error('Sign in failed:', error);
      return null;
    }
  }

  async signInWithEmail(email: string, password: string): Promise<{ user: User | null; error: string | null }> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();
      const user = await api.createSession(idToken);
      return { user, error: null };
    } catch (error: any) {
      console.error('Email sign in failed:', error);
      const errorMessage = this.getAuthErrorMessage(error.code);
      return { user: null, error: errorMessage };
    }
  }

  async signUpWithEmail(email: string, password: string): Promise<{ user: User | null; error: string | null }> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();
      const user = await api.createSession(idToken);
      return { user, error: null };
    } catch (error: any) {
      console.error('Email sign up failed:', error);
      const errorMessage = this.getAuthErrorMessage(error.code);
      return { user: null, error: errorMessage };
    }
  }

  /**
   * Single email-password entry point: signs in if the account exists, creates
   * it if not. This collapses the sign-in vs sign-up choice into a single form
   * for first-time invitees who don't know whether they have an account yet.
   *
   * Modern Firebase masks "user not found" as "invalid-credential" to prevent
   * email enumeration, so we attempt sign-up on either error code. If the
   * account already exists with a different password, the sign-up call returns
   * `auth/email-already-in-use`, which we surface as "Incorrect password".
   */
  async signInOrSignUpWithEmail(email: string, password: string): Promise<{ user: User | null; error: string | null }> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();
      const user = await api.createSession(idToken);
      return { user, error: null };
    } catch (error: any) {
      const code = error?.code as string | undefined;
      const accountMaybeMissing = code === 'auth/user-not-found' || code === 'auth/invalid-credential';
      if (!accountMaybeMissing) {
        console.error('Email sign in failed:', error);
        return { user: null, error: this.getAuthErrorMessage(code ?? '') };
      }
    }

    // First call failed with a "no such account" / masked credential error.
    // Try creating the account. If the email turns out to already exist,
    // the original failure was a bad password — say so clearly.
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();
      const user = await api.createSession(idToken);
      return { user, error: null };
    } catch (error: any) {
      console.error('Email sign up (auto-fallback) failed:', error);
      const code = error?.code as string | undefined;
      if (code === 'auth/email-already-in-use') {
        return { user: null, error: 'Incorrect password for this email. Use Forgot password to reset it.' };
      }
      return { user: null, error: this.getAuthErrorMessage(code ?? '') };
    }
  }

  async resetPassword(email: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, error: null };
    } catch (error: any) {
      console.error('Password reset failed:', error);
      const errorMessage = this.getAuthErrorMessage(error.code);
      return { success: false, error: errorMessage };
    }
  }

  private getAuthErrorMessage(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/invalid-credential':
        return 'Invalid email or password';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later';
      default:
        return 'An error occurred. Please try again';
    }
  }

  async signOut(): Promise<void> {
    try {
      await api.deleteSession();
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }

  isAdmin(): boolean {
    return this.state.user?.is_admin ?? false;
  }
}

export const authService = new AuthService();
